import {
  AfterViewInit,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ElementRef,
  effect,
  signal,
  OnInit,
} from '@angular/core';

import {
  NgbNav,
  NgbNavContent,
  NgbNavItem,
  NgbNavLink,
  NgbNavLinkButton,
  NgbNavOutlet,
  NgbOffcanvas,
  OffcanvasDismissReasons,
} from '@ng-bootstrap/ng-bootstrap';
import {
  DatePipe,
  NgClass,
  NgOptimizedImage,
  NgStyle,
  UpperCasePipe,
} from '@angular/common';
import { TaskService } from '@services/task.service';
import { TaskItemComponent } from '../task-item/task-item.component';
import { TaskStatus } from '../../../../enums/task-status';
import { Router, NavigationStart } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CreateTaskModal } from '../create-task-modal/create-task-modal.component';
import { SaveBtn } from '@shared/components/save-btn/save-btn.component';
import { TranslateModule } from '@ngx-translate/core';
import { SettingsService } from '@services/settings.service';
import { ToastService } from '@services/toast.service';
import { Task } from '@interfaces/task/task';
import { DataService } from '@services/data.service';
import { DataCategoryType } from 'src/app/enums/data-category-type';
import { environment } from 'src/environments/environment';
import { IconsImageComponent } from '../../../../shared/components/icons-image/icons-image.component';
import { LoadingSpinnerComponent } from '@shared/components/loading-spinner/loading-spinner.component';
import { TaskStore } from '@stores/task.store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-task-drawer',
  standalone: true,
  imports: [
    NgbNav,
    NgbNavItem,
    NgbNavLinkButton,
    NgbNavContent,
    NgbNavLink,
    NgbNavOutlet,
    NgStyle,
    NgOptimizedImage,
    TaskItemComponent,
    NgClass,
    MatDialogModule,
    SaveBtn,
    TranslateModule,
    UpperCasePipe,
    IconsImageComponent,
    LoadingSpinnerComponent,
  ],
  providers: [DatePipe],
  templateUrl: './task-drawer.component.html',
  styleUrl: './task-drawer.component.scss',
})
export class TaskDrawerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('canvasBody') canvasBody!: ElementRef;
  private lastScrollTop = 0;
  isScrollingDown = false;
  isWlmhEnabled = environment.isWlmhEnabled;
  selectedLocation = input('');
  closeResult = '';
  @ViewChild('content') drawerContent!: TemplateRef<any>;

  private isTaskLoading = false;
  private isNurseLoading = false;
  private isAssistReqLoading = false;
  private isAlarmLoading = false;

  private _activeId = signal(1);
  private destroy$ = new Subject<void>();
  private refreshIntervalId?: number;

  get activeId(): number {
    return this._activeId();
  }
  set activeId(value: number) {
    this._activeId.set(value);
    if (this.canvasBody) {
      this.canvasBody.nativeElement.scrollTop = 0;
    }
  }

  private router = inject(Router);
  private offcanvasService = inject(NgbOffcanvas);
  private taskService = inject(TaskService);
  private settingsService = inject(SettingsService);
  private toastService = inject(ToastService);
  isTaskDrawerPinned = this.settingsService.isTaskDrawerPinned;
  isTaskDrawerOpen = this.settingsService.isTaskDrawerOpen;
  taskPageInfo = computed(() => this.taskService.taskPageInfo());
  assistreqPageInfo = computed(() => this.taskService.assistreqPageInfo());
  nursePageInfo = computed(() => this.taskService.nursePageInfo());
  alarmPageInfo = computed(() => this.taskService.alarmPageInfo());
  drawerLoading = computed(() => this.taskService.drawerPending().length > 0);
  showNoTasks = computed(
    () => !this.drawerLoading() && this.taskService.tasks().length === 0,
  );

  inProgressTasks = computed(() => {
    return this.taskService
      .tasks()
      .filter((t) => {
        const selectedDept = this.settingsService.selectedDepartment() ?? '';

        const byStatus =
          TaskStatus.INITIATED === t.status ||
          TaskStatus.ACTIVE === t.status ||
          TaskStatus.NOTIFY === t.status ||
          TaskStatus.ESCALATED === t.status;

        if (!byStatus) return false;

        if (t.type === 'ALARM_NOTIFICATION') {
          return (
            !!selectedDept &&
            Array.isArray(t.data?.filterDepts) &&
            t.data.filterDepts.some(
              (fd: any) =>
                fd?.unit === selectedDept || fd?.department === selectedDept,
            )
          );
        }

        if (typeof t.data.location !== 'string') {
          const matchByLocationUnit =
            !!selectedDept && t.data.location?.unit === selectedDept;

          const matchByFilterDepts =
            !!selectedDept &&
            Array.isArray(t.data.filterDepts) &&
            t.data.filterDepts.some(
              (fd) =>
                fd?.unit === selectedDept ||
                (fd as any)?.department === selectedDept,
            );

          return matchByLocationUnit || matchByFilterDepts;
        }

        return true;
      })
      .sort(this.sortTasksByStartTime);
  });
  showNoInProgressTasks = computed(
    () => !this.drawerLoading() && this.inProgressTasks().length === 0,
  );

  completedTasks = computed(() => {
    return this.taskService
      .tasks()
      .filter((t) => {
        const selectedDept = this.settingsService.selectedDepartment() ?? '';
        const byStatus =
          TaskStatus.COMPLETE === t.status ||
          TaskStatus.UA_CLOSED === t.status ||
          TaskStatus.CLOSED === t.status;

        if (!byStatus) return false;

        if (t.type === 'ALARM_NOTIFICATION') {
          return (
            !!selectedDept &&
            Array.isArray(t.data?.filterDepts) &&
            t.data.filterDepts.some(
              (fd: any) =>
                fd?.unit === selectedDept || fd?.department === selectedDept,
            )
          );
        }

        if (typeof t.data.location !== 'string') {
          const matchByLocationUnit =
            !!selectedDept && t.data.location?.unit === selectedDept;

          const matchByFilterDepts =
            !!selectedDept &&
            Array.isArray(t.data.filterDepts) &&
            t.data.filterDepts.some(
              (fd) =>
                fd?.unit === selectedDept ||
                (fd as any)?.department === selectedDept,
            );

          return matchByLocationUnit || matchByFilterDepts;
        }
        return true;
      })
      .sort(this.sortTasksByStartTime);
  });
  showNoCompletedTasks = computed(
    () => !this.drawerLoading() && this.completedTasks().length === 0,
  );

  private dataService = inject(DataService);
  public dialog = inject(MatDialog);
  isLoading = false;

  taskStore = inject(TaskStore);

  public pagination = {
    page: 1,
    perPage: 20,
  };

  constructor() {
    effect(() => {
      const currentId = this._activeId();
      const department = this.settingsService.selectedDepartment();
      this.fetchDrawerData({ currentId, department, resetTasks: true });
    });

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationStart),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        if (!this.isTaskDrawerPinned()) {
          this.offcanvasService.dismiss('route-change');
          this.settingsService.openTaskDrawer(false);
        }
      });
  }

  ngOnInit(): void {
    this.startAutoRefresh();
  }

  openDialog(): void {
    this.dialog.closeAll();
    this.closeDrawer(this.drawerContent);
    this.dialog.open(CreateTaskModal, {
      minWidth: '350px',
    });
  }

  highlightTasks(event: MessageEvent) {
    if (this.isWlmhEnabled) return;
    if ('updateAssignmentBtnClick' === (event as any).data.event) {
      this.router.navigateByUrl('/assignments');
    } else {
      const locationName = (event as any).data;
      const isTaskExists = this.taskService.isTaskExists(locationName);
      this.taskService.toggleHighlightTasksByLocationName(locationName);
      if (!this.offcanvasService.hasOpenOffcanvas() && isTaskExists) {
        this.openDrawer(this.drawerContent);
      }
    }
  }

  ngAfterViewInit(): void {
    window.addEventListener('message', (e) => this.highlightTasks(e), false);
    if (this.isTaskDrawerPinned()) {
      this.openDrawer(this.drawerContent);
    }
  }

  ngOnDestroy(): void {
    if (!this.isTaskDrawerPinned()) {
      this.offcanvasService.dismiss('destroy');
      this.settingsService.openTaskDrawer(false);
    }
    if (this.refreshIntervalId !== undefined) {
      window.clearInterval(this.refreshIntervalId);
    }
    window.removeEventListener('message', (e) => this.highlightTasks(e), false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  openDrawer(content: TemplateRef<any>) {
    if (!this.offcanvasService.hasOpenOffcanvas()) {
      this.fetchDrawerData({
        currentId: this._activeId(),
        department: this.settingsService.selectedDepartment(),
        resetTasks: true,
      });
      this.settingsService.openTaskDrawer(true);
      this.offcanvasService
        .open(content, {
          ariaLabelledBy: 'offcanvas-basic-title',
          position: 'end',
          backdrop: false,
          animation: false,
          panelClass: 'dashboard-offcanvas-container',
        })
        .result.then(
          (result) => {
            this.closeResult = `Closed with: ${result}`;
          },
          (reason) => {
            this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
          },
        );
    }
  }

  closeDrawer(content: TemplateRef<any>) {
    if (!this.isTaskDrawerPinned()) {
      this.offcanvasService.dismiss('manual-close');
      this.settingsService.openTaskDrawer(false);
    }
  }

  dismissDrawer() {
    if (!this.isTaskDrawerPinned()) {
      this.closeDrawer(this.drawerContent);
    } else {
      this.toastService.showInfoMessage('Task drawer is pinned');
    }
  }

  private getDismissReason(reason: any): string {
    this.settingsService.openTaskDrawer(false);
    switch (reason) {
      case OffcanvasDismissReasons.ESC:
        return 'by pressing ESC';
      case OffcanvasDismissReasons.BACKDROP_CLICK:
        return 'by clicking on the backdrop';
      default:
        return `with: ${reason}`;
    }
  }

  pinUnpinDrawer() {
    this.settingsService.pinUnpinTaskDrawer();
  }

  private sortTasksByStartTime(a: Task, b: Task) {
    const timeA = new Date(a.data.startTime.replace(' UTC', '')).getTime();
    const timeB = new Date(b.data.startTime.replace(' UTC', '')).getTime();
    return timeB - timeA;
  }

  onScroll() {
    const element = this.canvasBody.nativeElement;
    const currentScrollTop = element.scrollTop;
    this.isScrollingDown = currentScrollTop > this.lastScrollTop;
    this.lastScrollTop = currentScrollTop;

    const isAtBottom =
      Math.abs(
        element.scrollHeight - element.scrollTop - element.clientHeight,
      ) < 1;

    if (
      isAtBottom &&
      (this.taskPageInfo().hasNextPage ||
        this.assistreqPageInfo().hasNextPage ||
        this.nursePageInfo().hasNextPage)
    ) {
      if (
        this.isTaskLoading ||
        this.isNurseLoading ||
        this.isAssistReqLoading
      ) {
        return;
      }

      this.isLoading = true;

      this.isTaskLoading = true;
      this.isNurseLoading = true;
      this.isAssistReqLoading = true;
      this.isAlarmLoading = true;

      Promise.all([
        (() => {
          this.dataService.sendDataRequest([
            {
              category: DataCategoryType.TASK,
              pagination: {
                page: this.taskPageInfo().page + 1,
                perPage: this.taskPageInfo().perPage,
              },
            },
          ]);
          this.isTaskLoading = false;
        })(),
        (() => {
          this.dataService.sendDataRequest([
            {
              category: DataCategoryType.NURSE,
              pagination: {
                page: this.nursePageInfo().page + 1,
                perPage: this.nursePageInfo().perPage,
              },
            },
          ]);
          this.isNurseLoading = false;
        })(),
        (() => {
          this.dataService.sendDataRequest([
            {
              category: DataCategoryType.ASSISTREQ,
              pagination: {
                page: this.assistreqPageInfo().page + 1,
                perPage: this.assistreqPageInfo().perPage,
              },
            },
          ]);
          this.isAssistReqLoading = false;
        })(),
        (() => {
          this.dataService.sendDataRequest([
            {
              category: DataCategoryType.ALARM,
              pagination: {
                page: this.alarmPageInfo().page + 1,
                perPage: this.alarmPageInfo().perPage,
              },
            },
          ]);
          this.isTaskLoading = false;
        })(),
      ]).finally(() => {
        setTimeout(() => {
          this.isLoading = false;
        }, 600);
      });
    }
  }

  private startAutoRefresh() {
    const refreshIntervalMs = 120000;
    this.refreshIntervalId = window.setInterval(() => {
      if (!this.isTaskDrawerPinned() && !this.isTaskDrawerOpen()) {
        return;
      }

      this.fetchDrawerData({
        currentId: this._activeId(),
        department: this.settingsService.selectedDepartment(),
        resetTasks: false,
      });
    }, refreshIntervalMs);
  }

  private fetchDrawerData({
    currentId,
    department,
    resetTasks,
  }: {
    currentId: number;
    department: string | undefined;
    resetTasks: boolean;
  }) {
    if (resetTasks) {
      this.taskStore.setTasks([], {
        hasNextPage: false,
        totalPages: 0,
        page: 1,
        perPage: 4,
      });
      this.taskService.startDrawerLoading();
    }

    const tasksFilters = {
      notificationStatus:
        currentId === 2
          ? [TaskStatus.UA_CLOSED, TaskStatus.COMPLETE]
          : currentId === 1
            ? [TaskStatus.INITIATED, TaskStatus.ACTIVE, TaskStatus.ESCALATED]
            : [],
      units: [department],
    };

    const nurseCallFilters = {
      notificationStatus:
        currentId === 2
          ? [TaskStatus.COMPLETE]
          : currentId === 1
            ? [TaskStatus.INITIATED, TaskStatus.ACTIVE, TaskStatus.NOTIFY]
            : [],
      units: [department],
    };

    const assistRequestFilters = {
      notificationStatus:
        currentId === 2
          ? [TaskStatus.COMPLETE, TaskStatus.UA_CLOSED]
          : currentId === 1
            ? [TaskStatus.INITIATED, TaskStatus.ACTIVE]
            : [],
      units: [department],
    };

    const alarmFilters = {
      notificationStatus:
        currentId === 2
          ? [TaskStatus.CLOSED]
          : currentId === 1
            ? [TaskStatus.INITIATED, TaskStatus.ACTIVE]
            : [],
      units: [department],
    };

    this.dataService.sendDataRequest([
      {
        category: DataCategoryType.TASK,
        pagination: this.pagination,
        filters: tasksFilters,
      },
      {
        category: DataCategoryType.NURSE,
        pagination: this.pagination,
        filters: nurseCallFilters,
      },
      {
        category: DataCategoryType.ASSISTREQ,
        pagination: this.pagination,
        filters: assistRequestFilters,
      },
      {
        category: DataCategoryType.ALARM,
        pagination: this.pagination,
        filters: alarmFilters,
      },
    ]);
  }
}
